/**
 * Site overview.
 *
 * Laid out as one scanning column plus a pinned rail. The column answers "what
 * should I do?" — score, counts, ranked recommendations. The rail answers
 * "what is the system doing?" — run status, issue mix, AI interpretation — and
 * stays in view because that context is what you check *while* reading the
 * column, not after scrolling past it.
 */
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/api/client';
import { AiAnalysisPanel } from '@/features/dashboard/AiAnalysisPanel';
import { ActivityCard } from '@/features/dashboard/ActivityCard';
import { HealthTrend } from '@/features/dashboard/HealthTrend';
import { IssueBreakdown } from '@/features/dashboard/IssueBreakdown';
import { RecommendationRow } from '@/features/dashboard/RecommendationRow';
import { aiApi, auditApi, crawlApi, siteApi } from '@/api/endpoints';
import { useAuth } from '@/app/auth';
import { Permission } from '@/app/permissions';
import {
  AlertIcon,
  BoltIcon,
  DocumentIcon,
  InfoIcon,
  PlayIcon,
  RefreshIcon,
  TrendIcon,
} from '@/components/icons';
import {
  Button,
  Callout,
  Card,
  CardHeader,
  CardLink,
  DetailLayout,
  DetailMain,
  DetailRail,
  EmptyState,
  ErrorState,
  LoadingState,
  MetricTile,
  PageHeader,
  ProgressBar,
  ScoreDial,
  Section,
} from '@/components/ui';
import {
  cn,
  formatNumber,
  formatPercent,
  severityBarClass,
} from '@/utils/format';
import type { DashboardResponse, IssueSeverity } from '@/types/api';
const SEVERITY_ORDER: IssueSeverity[] = ['critical', 'error', 'warning', 'opportunity', 'info'];
export function DashboardPage() {
  const { siteId = '' } = useParams();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const siteQuery = useQuery({ queryKey: ['site', siteId], queryFn: () => siteApi.get(siteId) });
  const dashboardQuery = useQuery({
    queryKey: ['dashboard', siteId],
    queryFn: () => siteApi.dashboard(siteId),
    // Poll while a crawl or audit is running so progress is visible live.
    refetchInterval: (query) => {
      const data = query.state.data as DashboardResponse | undefined;
      const running =
        ['queued', 'running', 'retrying'].includes(data?.last_crawl?.status ?? '') || ['queued', 'running', 'retrying'].includes(data?.last_audit?.status ?? '');
      return running ? 4000 : false;
    },
  });
  const onboardingQuery = useQuery({
    queryKey: ['onboarding', siteId],
    queryFn: () => siteApi.onboarding(siteId),
  });
  const aiQuery = useQuery({ queryKey: ['ai-status'], queryFn: () => aiApi.status() });
  const crawlMutation = useMutation({
    mutationFn: () => crawlApi.start(siteId, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dashboard', siteId] }),
  });
  const auditMutation = useMutation({
    mutationFn: () => auditApi.start(siteId, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dashboard', siteId] }),
  });
  if (siteQuery.isLoading || dashboardQuery.isLoading) return <LoadingState />;
  if (siteQuery.isError) return <ErrorState message={(siteQuery.error as Error).message} onRetry={() => void siteQuery.refetch()} />;
  if (dashboardQuery.isError) {
    return (
      <ErrorState
        message={(dashboardQuery.error as Error).message}
        onRetry={() => void dashboardQuery.refetch()}
      />
    );
  }
  const data = dashboardQuery.data!;
  const site = siteQuery.data!;
  const onboarding = onboardingQuery.data;
  const crawlRunning = data.last_crawl?.status === 'running';
  // Auditing needs a *completed* crawl: the API resolves the most recent one
  // and returns "run a crawl first" when there is none. Enabling the button
  // while the first crawl is still going invites a click that always fails.
  const crawlInFlight = data.last_crawl?.status === 'running' || data.last_crawl?.status === 'queued';
  const auditRunning = ['queued', 'running', 'retrying'].includes(data.last_audit?.status ?? '');
  const canAudit = data.last_crawl?.status === 'completed' && !crawlInFlight;
  const mutationError = [crawlMutation.error, auditMutation.error].find(Boolean);
  const healthTone =
    data.health_score === null
      ? 'neutral'
      : data.health_score >= 75
        ? 'success'
        : data.health_score >= 55
          ? 'warning'
          : 'danger';
  // The search-performance card already carries the "connect Search Console"
  // ask, so repeating it in a banner above is noise, not emphasis.
  const showOnboarding =
    onboarding &&
    onboarding.next_step !== 'review_actions' &&
    onboarding.next_step !== 'connect_search_console' &&
    !crawlRunning &&
    !auditRunning;
  return (
    <>
      <PageHeader
        title={site.name}
        description={site.normalized_origin}
        actions={
          <>
            <Link className="inline-flex items-center gap-2 rounded-lg border border-brand-border bg-brand-subtle px-4 py-2 text-sm font-medium text-brand" to={`/sites/${siteId}/reports`}><DocumentIcon className="h-4 w-4" /> Website report</Link>
            {can(Permission.CRAWL_RUN) ? (
              <Button
                onClick={() => crawlMutation.mutate()}
                loading={crawlMutation.isPending}
                // Queued counts as in flight: connecting a website starts a
                // crawl straight away, and offering the button in that window
                // only produces a rejected duplicate.
                disabled={crawlInFlight}
                icon={<RefreshIcon className="h-4 w-4" />}
              >
                {crawlInFlight ? 'Crawling…' : 'Run crawl'}
              </Button>
            ) : null}
            {can(Permission.AUDIT_RUN) ? (
              <Button
                variant="primary"
                onClick={() => auditMutation.mutate()}
                loading={auditMutation.isPending}
                disabled={auditRunning || !canAudit}
                title={
                  !data.last_crawl
                    ? 'Run a crawl first'
                    : crawlInFlight
                      ? 'Waiting for the crawl to finish'
                      : undefined
                }
                icon={<PlayIcon className="h-4 w-4" />}
              >
                {auditRunning ? 'Auditing…' : 'Run audit'}
              </Button>
            ) : null}
          </>
        }
      />
      <div className="space-y-4">
        {mutationError ? (
          <Callout tone="danger" icon={<AlertIcon className="h-5 w-5" />}>
            {mutationError instanceof ApiError
              ? mutationError.message
              : 'The request could not be completed.'}
          </Callout>
        ) : null}
        {crawlRunning && data.last_crawl ? (
          <Card>
            <CardHeader
              title="Crawl in progress"
              description={data.last_crawl.current_activity ?? 'Working…'}
              icon={<RefreshIcon className="h-4 w-4 animate-spin" />}
            />
            <ProgressBar
              percent={data.last_crawl.progress_percent}
              label={`${data.last_crawl.pages_crawled} of ${data.last_crawl.pages_discovered} discovered pages fetched`}
            />
          </Card>
        ) : null}
        {showOnboarding ? (
          <OnboardingCallout
            nextStep={onboarding.next_step}
            siteId={siteId}
            hasCrawl={onboarding.first_crawl_completed}
          />
        ) : null}
      </div>
      <DetailLayout className="mt-6">
        {/* ------------------------------------------------ scanning column */}
        <DetailMain>
          <Card padded={false} className="dashboard-health-card overflow-hidden">
            <div className="dashboard-health-grid grid sm:grid-cols-[15rem_minmax(0,1fr)]">
              <div className="dashboard-score-panel flex flex-col items-center justify-center border-b border-border-subtle bg-gradient-to-b from-surface-muted/60 to-surface px-5 py-6 sm:border-r sm:border-b-0">
                <p className="mb-2 text-[11px] font-semibold tracking-wider text-ink-subtle uppercase">
                  SEO health
                </p>
                <ScoreDial
                  score={data.health_score}
                  band={data.health_score_band}
                  tone={healthTone}
                />
                <HealthTrend
                  score={data.health_score}
                  previous={data.previous_health_score}
                  hasAudit={Boolean(data.last_audit)}
                />
              </div>
              {/* Four counts that carry information, rather than three that
                  read zero while the real number sits in the small print. */}
              <div className="dashboard-metric-grid grid grid-cols-2 divide-x divide-y divide-border-subtle">
                <MetricCell
                  to={`/sites/${siteId}/issues`}
                  label="Open issues"
                  value={data.total_open_issues}
                  tone={data.critical_issues > 0 ? 'danger' : 'neutral'}
                  icon={<AlertIcon className="h-4 w-4" />}
                  detail={
                    data.critical_issues > 0
                      ? `${data.critical_issues} critical or error`
                      : 'none critical'
                  }
                  distribution={data.issue_distribution}
                />
                <MetricCell
                  to={`/sites/${siteId}/opportunities`}
                  label="Recommendations"
                  value={data.total_recommendations}
                  tone="brand"
                  icon={<TrendIcon className="h-4 w-4" />}
                  detail={`${data.high_impact_opportunities} high-impact`}
                />
                <MetricCell
                  to={`/sites/${siteId}/actions?status=pending_approval`}
                  label="Awaiting approval"
                  value={data.actions_awaiting_approval}
                  tone={data.actions_awaiting_approval > 0 ? 'warning' : 'neutral'}
                  icon={<BoltIcon className="h-4 w-4" />}
                  detail={`${data.actions_completed_30d} applied in 30 days`}
                />
                <MetricCell
                  to={`/sites/${siteId}/pages`}
                  label="Pages crawled"
                  value={data.last_crawl?.pages_crawled ?? 0}
                  tone="neutral"
                  icon={<DocumentIcon className="h-4 w-4" />}
                  detail={
                    data.last_crawl
                      ? `${formatNumber(data.last_crawl.pages_discovered)} discovered`
                      : 'no crawl yet'
                  }
                />
              </div>
            </div>
            {/* The health score is ours; never let it be mistaken for a Google metric. */}
            <p className="border-t border-border-subtle bg-surface-muted/40 px-5 py-3 text-xs text-ink-subtle">
              {data.health_score_note}
            </p>
          </Card>
          <Section title="Search performance">
            {data.search_data_available ? (
              <div className="grid gap-4 sm:grid-cols-3">
                <MetricTile
                  label="Organic clicks"
                  value={data.organic_clicks ? formatNumber(data.organic_clicks.value) : '—'}
                  delta={data.organic_clicks?.change_percent ?? null}
                />
                <MetricTile
                  label="Impressions"
                  value={data.impressions ? formatNumber(data.impressions.value) : '—'}
                  delta={data.impressions?.change_percent ?? null}
                />
                <MetricTile
                  label="Average CTR"
                  value={data.average_ctr ? formatPercent(data.average_ctr.value) : '—'}
                  delta={data.average_ctr?.change_percent ?? null}
                />
              </div>
            ) : (
              <Callout
                tone="info"
                title="Connect Google Search Console"
                icon={<InfoIcon className="h-5 w-5" />}
                action={
                  <Link
                    to="/integrations"
                    className="inline-flex rounded-lg bg-surface px-3 py-1.5 text-sm font-medium whitespace-nowrap text-brand shadow-card hover:bg-brand-subtle"
                  >
                    Connect
                  </Link>
                }
              >
                Clicks, impressions and CTR appear here once connected. Search data also unlocks the
                highest-value findings: click-through gaps, striking-distance queries and content
                decay.
              </Callout>
            )}
          </Section>
          <Card>
            <CardHeader
              title="Top recommendations"
              description="Ranked by expected impact, confidence, effort and business value."
              action={<CardLink to={`/sites/${siteId}/opportunities`}>View all</CardLink>}
            />
            {data.top_recommendations.length === 0 ? (
              <EmptyState
                icon={<TrendIcon className="h-5 w-5" />}
                title="No recommendations yet"
                description={
                  data.last_audit
                    ? 'The last audit found nothing that met the priority threshold.'
                    : 'Run a crawl and an audit to generate recommendations.'
                }
              />
            ) : (
              <ul className="-mx-2 divide-y divide-border-subtle">
                {data.top_recommendations.map((recommendation) => (
                  <RecommendationRow
                    key={recommendation.id}
                    recommendation={recommendation}
                    siteId={siteId}
                  />
                ))}
              </ul>
            )}
          </Card>
        </DetailMain>
        {/* --------------------------------------------------- pinned rail */}
        <DetailRail>
          <ActivityCard crawl={data.last_crawl} audit={data.last_audit} />

          <Card>
            <CardHeader
              title="Issue breakdown"
              icon={<AlertIcon className="h-4 w-4" />}
              action={<CardLink to={`/sites/${siteId}/issues`}>All</CardLink>}
            />
            <IssueBreakdown
              distribution={data.issue_distribution}
              categories={data.issue_categories}
              siteId={siteId}
            />
          </Card>

          {aiQuery.data ? (
            <AiAnalysisPanel
              siteId={siteId}
              status={aiQuery.data}
              hasAudit={Boolean(data.last_audit)}
            />
          ) : null}
        </DetailRail>
      </DetailLayout>
    </>
  );
}
/**
 * One counter in the overview grid.
 *
 * These are navigation as much as measurement, so the whole cell is the target
 * and the number leads. ``detail`` carries the qualifier that used to be the
 * headline — "3 high-impact" is context for "5 recommendations", not a
 * replacement for it.
 */
function MetricCell({
  to,
  label,
  value,
  detail,
  tone,
  icon,
  distribution,
}: {
  to: string;
  label: string;
  value: number;
  detail: string;
  tone: 'neutral' | 'danger' | 'warning' | 'brand';
  icon: ReactNode;
  distribution?: Record<string, number>;
}) {
  const valueTone = {
    neutral: 'text-ink',
    danger: 'text-danger',
    warning: 'text-warning',
    brand: 'text-brand',
  }[tone];
  const bars = distribution
    ? SEVERITY_ORDER.map((severity) => ({ severity, count: distribution[severity] ?? 0 })).filter(
        (row) => row.count > 0,
      )
    : [];
  const total = bars.reduce((sum, row) => sum + row.count, 0);
  return (
    <Link
      to={to}
      className="dashboard-metric-cell group flex min-w-0 flex-col justify-between gap-3 p-4 transition-colors hover:bg-brand-subtle/30 sm:p-5"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[11px] font-medium tracking-wide text-ink-subtle uppercase">
          {label}
        </span>
        <span className="shrink-0 text-ink-subtle transition-colors group-hover:text-brand">
          {icon}
        </span>
      </div>
      <div>
        <p className={cn('tnum text-3xl leading-none font-semibold', valueTone)}>
          {formatNumber(value)}
        </p>
        {bars.length > 0 && total > 0 ? (
          // A single stacked bar shows the mix without spending four rows on it.
          <div className="mt-2.5 flex h-1.5 overflow-hidden rounded-full bg-surface-sunken">
            {bars.map(({ severity, count }) => (
              <span
                key={severity}
                className={severityBarClass[severity]}
                style={{ width: `${(count / total) * 100}%` }}
                title={`${count} ${severity}`}
              />
            ))}
          </div>
        ) : null}
        <p className="mt-2 truncate text-xs text-ink-subtle">{detail}</p>
      </div>
    </Link>
  );
}
function OnboardingCallout({
  nextStep,
  siteId,
  hasCrawl,
}: {
  nextStep: string;
  siteId: string;
  hasCrawl: boolean;
}) {
  const steps: Record<
    string,
    { title: string; body: string; cta?: { label: string; to: string } }
  > = {
    run_first_crawl: {
      title: 'Run your first crawl',
      body: 'We will discover your pages and collect every SEO signal from them. Nothing is modified.',
    },
    run_first_audit: {
      title: 'Run your first audit',
      body: 'The crawl is done. Run an audit to turn it into prioritised, evidence-backed findings.',
    },
    connect_search_console: {
      title: 'Connect Google Search Console',
      body: 'Search data unlocks the highest-value findings: click-through gaps, striking-distance queries and content decay.',
      cta: { label: 'Connect Search Console', to: '/integrations' },
    },
    add_business_context: {
      title: 'Add your business context',
      body: 'Telling us what you sell lets prioritisation weight your commercially important pages correctly.',
      cta: { label: 'Add context', to: `/sites/${siteId}/settings` },
    },
    generate_recommendations: {
      title: 'Generate recommendations',
      body: 'Run an audit to convert findings into concrete, prioritised recommendations.',
    },
    connect_executor: {
      title: 'Connect WordPress or GitHub',
      body: 'Connect where your site lives and SEO RankFix can apply approved changes for you, then verify them.',
      cta: { label: 'Connect', to: '/integrations' },
    },
  };
  const step = steps[nextStep];
  if (!step) return null;
  return (
    <Callout
      tone={hasCrawl ? 'info' : 'warning'}
      title={step.title}
      icon={<InfoIcon className="h-5 w-5" />}
      action={
        step.cta ? (
          <Link
            to={step.cta.to}
            className="inline-flex rounded-lg bg-surface px-3 py-1.5 text-sm font-medium whitespace-nowrap text-brand shadow-card hover:bg-brand-subtle"
          >
            {step.cta.label}
          </Link>
        ) : undefined
      }
    >
      {step.body}
    </Callout>
  );
}
